import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { forwardRef, type ReactElement, type ReactNode } from 'react';
import { Button, type ButtonProps } from 'semantic-ui-react';

import s from './toolbar-button.scss';

export interface ToolbarButtonProps extends Omit<
  ButtonProps,
  /**
   * Presentational props are owned by the component so the toolbar
   * styling stays consistent across every consumer — callers can't
   * opt out of the transparent / fixed-height visual identity.
   */
  | 'basic'
  | 'color'
  | 'compact'
  | 'fluid'
  | 'icon'
  | 'inverted'
  | 'primary'
  | 'secondary'
  | 'size'
> {
  /**
   * Button label. Omit for an icon-only button.
   */
  content?: ReactNode;
  /**
   * A lucide icon element to render before the label.
   */
  icon?: ReactElement;
}

/**
 * A standardized button for table/list toolbars — Filter, Export, Print, etc.
 * Renders a Semantic UI Button with a consistent 2.5rem height and a slot for
 * a lucide icon next to the label.
 *
 * When `content` is omitted, the button renders in a square icon-only variant
 * suitable for compact toolbars. Consumers should pass an `aria-label` in that
 * case so the button has an accessible name.
 *
 * @example
 * <ToolbarButton icon={<ArrowUpFromLine />} content="Export" onClick={…} />
 *
 * @example
 * // Icon-only
 * <ToolbarButton icon={<Printer />} aria-label="Print" onClick={…} />
 */
export const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  ({ className, content, icon, ...buttonProps }, ref) => {
    useStyles(s);

    const iconOnly = content == null && icon != null;

    return (
      <Button
        ref={ref}
        className={classNames(s.root, className)}
        {...buttonProps}
        compact
        icon={iconOnly || undefined}
      >
        {icon}
        {content}
      </Button>
    );
  },
);

ToolbarButton.displayName = 'ToolbarButton';

export default ToolbarButton;
